import { ChangeDetectionStrategy, Component, EventEmitter, inject, Input, OnChanges, Output } from '@angular/core';
import { decodeHtmlEntities } from 'src/app/_core/utils/decode-html-entities';
import { Search, SearchTerm } from 'src/app/state/user/searches/models/search';
import { MatLegacyTableDataSource as MatTableDataSource } from '@angular/material/legacy-table';
import { SearchModel } from 'src/app/models/search-model';
import { Params, Router } from '@angular/router';
import { Filter } from 'src/app/models/filter';
import { Util } from 'src/app/util';
import { LocaleService } from 'src/app/services/locale.service';
import { SearchesFacade } from 'src/app/state/user/searches/searches.facade';
import { ToastService } from '../../../_core/services/toast.service';
import { AnalyticsService } from '../../../_core/analytics/analytics.service';

@Component({
  selector: 'app-user-search-table',
  templateUrl: './user-search-table.component.html',
  styleUrls: ['./user-search-table.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserSearchTableComponent implements OnChanges {
  private readonly toastService = inject(ToastService);
  private readonly analyticsService = inject(AnalyticsService);

  @Input()
  searchesHistory: Search[];

  @Input()
  savedSearches: Search[];

  @Input()
  history: boolean;

  @Output()
  saved = new EventEmitter<void>();

  dataSource: MatTableDataSource<Search>;
  displayedColumns: string[] = ['query', 'facets', 'total', 'date', 'actions'];
  searchModel = new SearchModel();

  constructor(
    private localeService: LocaleService,
    private router: Router,
    private searchesFacade: SearchesFacade
  ) {}

  ngOnChanges(): void {
    this.updateTable();
  }

  updateTable() {
    const searches = this.searchesHistory ?? this.savedSearches;
    searches?.forEach((element) => {
      if (element.query) {
        element.query = decodeHtmlEntities(element.query);
      }

      element.searchTerms = this.parseQuery(element.query).filter((q) => q);
      element.filters = this.parseFilters(element.facets);
    });

    this.dataSource = new MatTableDataSource<Search>(searches);
  }

  parseQuery(query: string): SearchTerm[] {
    if (!query) {
      return [];
    }

    const elements: string[] = query.split(SearchModel.AND_KEYWORD);

    return elements.map((element) => {
      const parts: string[] = element.split(':');

      // legacy...
      if (parts.length === 1) {
        parts.unshift(Util.ALLFIELDS);
      } else if (parts[0] === 'allfields') {
        parts[0] = Util.ALLFIELDS;
      }

      const criteria = this.searchModel.getSearchCriterias().find((c) => c.field === parts[0]);

      if (criteria) {
        return {
          term: parts[1].replace(/"/g, ''),
          field: criteria.field,
          icon: this.searchModel.getTermIcon(criteria.field),
        };
      }
    });
  }

  parseFilters(filters: string): Partial<Filter>[] {
    if (!filters) {
      return [];
    }

    const elements = filters.match(Util.QUOTED_TERMS_PATTERN);

    return elements?.map((element) => {
      const parts: string[] = element.split(':');
      const partial: Partial<Filter> = {
        label: parts[1].replace(/"/g, ''),
      };

      return partial;
    });
  }

  repeatSearch(search: Search) {
    const queryParams: Params = {
      query: search.query,
      facets: search.facets,
    };

    if (search.collectionId) {
      queryParams.collectionId = search.collectionId;
    }

    if (search.careerPlanLevelId) {
      queryParams.careerPlanLevelId = search.careerPlanLevelId;
    }

    if (search.challengeId) {
      queryParams.challengeId = search.challengeId;
    }

    if (this.history) {
      this.analyticsService.searchPerformedOrigin = 'search_history';
    }

    this.router.navigate(['/results'], {
      queryParams,
    });
  }

  deleteSearch(search: Search, event: MouseEvent) {
    event.stopPropagation();

    this.searchesFacade.deleteSearch(search.id).subscribe(() => {
      this.toastService.showToast('messages.search.remove');
    });
  }

  saveSearch(search: Search, event: MouseEvent) {
    event.stopPropagation();

    if (this.searchesFacade.isSearchSaved(search)) {
      return this.toastService.showToast('messages.search.duped', 'info');
    }

    this.searchesFacade.postSearch(search).subscribe({
      next: () => {
        this.saved.emit();
        this.toastService.showToast('messages.search.saved');
        this.analyticsService.sendUserActionEvent('search_saved', true);
      },
      error: () => this.analyticsService.sendUserActionEvent('search_saved', false),
    });
  }

  copyToClipboard(search: Search, event: MouseEvent) {
    event.stopPropagation();
    const shareUrl = new URL(Util.getOrigin() + '/results');

    if (search.query) {
      shareUrl.searchParams.append('query', search.query);
    }

    if (search.facets) {
      shareUrl.searchParams.append('facets', search.facets);
    }

    Util.copyToClipboard(shareUrl.href);
    this.toastService.showToast('messages.copied', 'info');
    this.analyticsService.sendUserActionEvent('search_link_copied', true);
  }

  getSearchTerms(terms: SearchTerm[]) {
    return terms?.map((t) => t.term).join(' ');
  }

  getFiltersLabels(filters: Partial<Filter>[]) {
    return filters?.map((f) => f.label).join(' ');
  }
}
